/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Authentication Context
 * 
 * Manages user authentication state, login/logout functionality,
 * and JWT token management for the Skill Shop platform.
 */

import React, { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { createMutationFn, skillShopFetch } from '@shared/lib/api';
import type { 
  LoginResponseData, 
  RegisterResponseData, 
  MeResponseData 
} from '@shared/types/responses/auth';
import type { 
  LoginRequestData, 
  RegisterRequestData 
} from '@shared/types/requests/auth';
import type { User } from '@shared/types/user';
import type { AuthContextType, SocialProvider } from '@shared/types/auth';
import { signInWithPopup, GoogleAuthProvider, OAuthProvider, signOut } from 'firebase/auth';
import { initializeFirebase } from '@shared/lib/firebase';
import { isAuthDisabled, isElectronApp } from '@shared/utils/env';
import { createMockAdminUser } from '@shared/utils/auth';
import { isRemoteAuthEnabled } from '@shared/utils/remoteAuth';
import { getMasterAuthEndpointUrl } from '@shared/services/masterServerConfig';
import { syncOrgSettingsToPlatform } from '@shared/lib/api/organization_settings';

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRemoteAuth] = useState(() => {
    const remoteAuth = isRemoteAuthEnabled();
    const isElectron = isElectronApp();
    console.log(`[AuthContext] Initialized - isRemoteAuth: ${remoteAuth}, isElectron: ${isElectron}`);
    if (typeof window !== 'undefined') {
      console.log('[AuthContext] window.__REMOTE_AUTH_CONFIG__:', (window as any).__REMOTE_AUTH_CONFIG__);
    }
    return remoteAuth;
  });

  const isAuthenticated = !!user;

  /**
   * Sync organization settings to platform settings.
   * This applies organization-level settings (like Vertex Anthropic config)
   * to the platform settings so they take effect for LLM operations.
   */
  const syncOrgSettingsToPlat = async (): Promise<void> => {
    try {
      const syncFn = syncOrgSettingsToPlatform();
      const result = await syncFn(undefined);
      if (result.synced && result.synced.length > 0) {
        console.log('Synced org settings to platform:', result.synced);
      }
    } catch (error) {
      // Don't fail login if org settings sync fails - just log the error
      console.warn('Failed to sync org settings to platform:', error);
    }
  };

  /**
   * Set the logged-in user ID in the system settings.
   * This should be called AFTER syncing user data from master server,
   * using the LOCAL user ID (not the master server's ID).
   */
  const setLoggedInUserId = async (userId: number): Promise<void> => {
    try {
      const response = await fetch('/api/settings/system/logged-in-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: JSON.stringify({ userId })
      });

      if (!response.ok) {
        console.error('Failed to set logged-in user ID:', await response.text());
      } else {
        console.log('Set logged-in user ID:', userId);
      }
    } catch (error) {
      console.error('Error setting logged-in user ID:', error);
    }
  };

  /**
   * Look up the LOCAL user ID by email.
   * This is needed in remote auth mode because the master server's user ID
   * is different from the local database's user ID.
   * 
   * @param email - The user's email address
   * @returns The LOCAL database user ID, or null if not found
   */
  const getLocalUserIdByEmail = async (email: string): Promise<number | null> => {
    try {
      const response = await fetch(`/api/auth/lookup-by-email?email=${encodeURIComponent(email)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error('Failed to lookup local user by email:', await response.text());
        return null;
      }

      const result = await response.json();
      if (result.success && result.data?.id) {
        console.log(`Found local user ID ${result.data.id} for email ${email}`);
        return result.data.id;
      }

      return null;
    } catch (error) {
      console.error('Error looking up local user by email:', error);
      return null;
    }
  };

  /**
   * Sync user data from master server to local database.
   * This creates or updates a local user record with the master user's data.
   * 
   * IMPORTANT: This function performs DATA SYNC only. It does NOT:
   * - Determine which user is logged in locally
   * - Set the logged_in_user_id setting
   * 
   * The logged-in user must be set BEFORE calling this function using setLoggedInUserId().
   * 
   * Supports multi-organization membership:
   * - organizationId: Default organization (backwards compatible)
   * - organizationMemberships: Array of all organization memberships (for multi-org support)
   */
  const syncUserFromMaster = async (masterUserData: any): Promise<void> => {
    try {
      // Build sync payload with support for multi-org memberships
      const syncPayload: Record<string, any> = {
        masterUserId: masterUserData.id.toString(),
        name: masterUserData.name,
        email: masterUserData.email,
        role: masterUserData.role,
        photoUrl: masterUserData.photoUrl,
        bio: masterUserData.bio,
        emailVerified: masterUserData.emailVerified,
        isActive: masterUserData.isActive,
        organizationId: masterUserData.organizationId,
      };
      
      // Include organization memberships if available from master server
      // This enables full multi-org sync with roles and default org
      if (masterUserData.organizationMemberships && Array.isArray(masterUserData.organizationMemberships)) {
        syncPayload.organizationMemberships = masterUserData.organizationMemberships;
        console.log(`Syncing ${masterUserData.organizationMemberships.length} organization memberships from master`);
      } else if (masterUserData.organizations && Array.isArray(masterUserData.organizations)) {
        // Alternative field name some APIs might use
        syncPayload.organizationMemberships = masterUserData.organizations;
        console.log(`Syncing ${masterUserData.organizations.length} organization memberships from master`);
      }
      
      const response = await fetch('/api/users/sync-from-master', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: JSON.stringify(syncPayload)
      });

      if (!response.ok) {
        console.error('Failed to sync user from master:', await response.text());
      } else {
        const result = await response.json();
        if (result.success) {
          // Log sync statistics (the endpoint now returns stats, not user data)
          console.log('Sync from master completed:', result.data);
          
          // Sync org settings to platform after user sync
          await syncOrgSettingsToPlat();
        }
      }
    } catch (error) {
      console.error('Error syncing user from master:', error);
    }
  };

  // Periodic re-validation timer reference
  const revalidationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const REVALIDATION_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes

  /**
   * Validate account with master server.
   * This is MANDATORY for Electron apps on every launch and periodically during use.
   * 
   * @param masterUserId - The user's ID on the master server
   * @param accessToken - The current access token
   * @returns true if account is valid, false otherwise
   */
  const validateAccountWithMaster = async (masterUserId: string, accessToken: string): Promise<boolean> => {
    try {
      console.log('[AuthContext] Validating account with master server...');
      
      const validateUrl = getMasterAuthEndpointUrl('VALIDATE_ACCOUNT');
      const response = await fetch(`${validateUrl}?masterUserId=${encodeURIComponent(masterUserId)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error(`[AuthContext] Account validation failed with status ${response.status}`);
        return false;
      }

      const result = await response.json();
      if (result.success && result.data?.valid) {
        console.log('[AuthContext] Account validated successfully');
        return true;
      }

      console.error('[AuthContext] Account validation returned invalid:', result.data?.reason || 'Unknown reason');
      return false;
    } catch (error) {
      console.error('[AuthContext] Account validation error:', error);
      return false;
    }
  };

  /**
   * Start periodic re-validation timer.
   * This ensures the account remains valid during app use.
   */
  const startRevalidationTimer = (masterUserId: string) => {
    // Clear any existing timer
    if (revalidationTimerRef.current) {
      clearInterval(revalidationTimerRef.current);
    }

    console.log('[AuthContext] Starting periodic re-validation timer (every 30 minutes)');
    
    revalidationTimerRef.current = setInterval(async () => {
      console.log('[AuthContext] Periodic re-validation triggered');
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        console.log('[AuthContext] No access token, skipping re-validation');
        return;
      }

      const isValid = await validateAccountWithMaster(masterUserId, token);
      
      if (!isValid) {
        console.log('[AuthContext] Periodic re-validation failed - logging out user');
        // Clear timer
        if (revalidationTimerRef.current) {
          clearInterval(revalidationTimerRef.current);
          revalidationTimerRef.current = null;
        }
        // Logout and redirect
        await logout();
      }
    }, REVALIDATION_INTERVAL_MS);
  };

  /**
   * Stop periodic re-validation timer.
   */
  const stopRevalidationTimer = () => {
    if (revalidationTimerRef.current) {
      clearInterval(revalidationTimerRef.current);
      revalidationTimerRef.current = null;
      console.log('[AuthContext] Stopped periodic re-validation timer');
    }
  };

  // Check for existing tokens on mount and validate account
  useEffect(() => {
    // Check if authentication is disabled in development
    if (isAuthDisabled()) {
      console.log('Authentication disabled - using mock user');
      setUser(createMockAdminUser());
      setIsLoading(false);
      return;
    }

    const initAuth = async () => {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setIsLoading(false);
        return;
      }

      const isElectron = isElectronApp();
      
      // In Electron mode with remote auth, we MUST validate the account with master server
      if (isElectron && isRemoteAuth) {
        console.log('[AuthContext] Electron mode with remote auth - mandatory account validation required');
        
        // First, get the local user to find their masterUserId
        try {
          const meResponse = await fetch('/api/auth/me', {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (!meResponse.ok) {
            console.log('[AuthContext] Failed to get local user - redirecting to signin');
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            setUser(null);
            setIsLoading(false);
            return;
          }

          const meResult = await meResponse.json();
          if (!meResult.success || !meResult.data) {
            console.log('[AuthContext] Invalid local user response - redirecting to signin');
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            setUser(null);
            setIsLoading(false);
            return;
          }

          const localUser = meResult.data;
          const masterUserId = localUser.masterUserId || localUser.id?.toString();

          if (!masterUserId) {
            console.error('[AuthContext] No masterUserId found for local user - cannot validate');
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            setUser(null);
            setIsLoading(false);
            return;
          }

          // MANDATORY: Validate account with master server
          const isValid = await validateAccountWithMaster(masterUserId, token);
          
          if (!isValid) {
            console.log('[AuthContext] Account validation failed - redirecting to signin');
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            setUser(null);
            setIsLoading(false);
            return;
          }

          // Account is valid - set user and start periodic re-validation
          const user: User = {
            ...localUser,
            createdAt: localUser.createdAt ? new Date(localUser.createdAt) : null,
            updatedAt: localUser.updatedAt ? new Date(localUser.updatedAt) : null,
            lastLoginAt: localUser.lastLoginAt ? new Date(localUser.lastLoginAt) : null,
          };
          setUser(user);
          
          // Start periodic re-validation timer
          startRevalidationTimer(masterUserId);
          
          // Sync org settings to platform
          await syncOrgSettingsToPlat();
          
          setIsLoading(false);
          return;
        } catch (error) {
          console.error('[AuthContext] Error during Electron auth init:', error);
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          setUser(null);
          setIsLoading(false);
          return;
        }
      }

      // Standard flow for web mode
      refreshUser();
    };

    initAuth();

    // Cleanup timer on unmount
    return () => {
      stopRevalidationTimer();
    };
  }, []);

  // Track refresh attempts to prevent infinite loops
  const refreshAttemptRef = useRef({ count: 0, lastAttempt: 0 });
  const MAX_REFRESH_ATTEMPTS = 3;
  const REFRESH_ATTEMPT_RESET_MS = 10000; // Reset counter after 10 seconds

  // Refresh access token using refresh token
  const refreshAccessToken = async (): Promise<boolean> => {
    // Check if authentication is disabled in development
    if (isAuthDisabled()) {
      console.log('[AuthContext] Authentication disabled - skipping token refresh');
      return true; // Return true to indicate "success" (no refresh needed)
    }

    // Prevent infinite refresh loops
    const now = Date.now();
    if (now - refreshAttemptRef.current.lastAttempt > REFRESH_ATTEMPT_RESET_MS) {
      refreshAttemptRef.current.count = 0;
    }
    refreshAttemptRef.current.lastAttempt = now;
    refreshAttemptRef.current.count++;
    
    if (refreshAttemptRef.current.count > MAX_REFRESH_ATTEMPTS) {
      console.error(`[AuthContext] Token refresh failed after ${MAX_REFRESH_ATTEMPTS} attempts - preventing infinite loop`);
      return false;
    }

    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        console.log('[AuthContext] No refresh token available');
        return false;
      }

      // Use master server for refresh in remote auth mode
      const refreshUrl = isRemoteAuth 
        ? getMasterAuthEndpointUrl('REFRESH')
        : '/api/auth/refresh';

      console.log(`[AuthContext] Refreshing token via ${isRemoteAuth ? 'master server' : 'local server'}: ${refreshUrl}`);

      const response = await fetch(refreshUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken })
      });

      if (!response.ok) {
        console.log(`[AuthContext] Token refresh failed with status ${response.status}`);
        return false;
      }

      const result = await response.json();
      if (result.success) {
        // Store new tokens
        localStorage.setItem('accessToken', result.data.accessToken);
        localStorage.setItem('refreshToken', result.data.refreshToken);
        console.log('[AuthContext] Access token refreshed successfully');
        // Reset attempt counter on success
        refreshAttemptRef.current.count = 0;
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('[AuthContext] Failed to refresh token:', error);
      return false;
    }
  };

  // Get current user profile with automatic token refresh
  const refreshUser = async () => {
    try {
      setIsLoading(true);
      let token = localStorage.getItem('accessToken');
      
      if (!token) {
        setUser(null);
        return;
      }

      // In Electron mode, ALWAYS use local server's /api/auth/me
      // The local server uses logged_in_user_id to return the correct LOCAL user
      // This ensures the user ID matches the local database, not the master server's ID
      const isElectron = isElectronApp();
      
      // Use master server for user profile in remote auth mode (web only)
      // In Electron mode, use local server even with remote auth enabled
      const meUrl = (isRemoteAuth && !isElectron)
        ? getMasterAuthEndpointUrl('ME')
        : '/api/auth/me';

      // First attempt with current token
      let response = await fetch(meUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // If unauthorized, try to refresh token and retry
      if (response.status === 401) {
        console.log('Access token expired, attempting refresh...');
        const refreshed = await refreshAccessToken();
        
        if (refreshed) {
          // Retry with new token
          token = localStorage.getItem('accessToken');
          if (token) {
            response = await fetch(meUrl, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
          }
        }
      }

      if (!response.ok) {
        throw new Error('Failed to fetch user after token refresh attempt');
      }

      const result = await response.json();
      if (result.success) {
        // Convert date strings to Date objects to match our User interface
        const userData = result.data;
        const user: User = {
          ...userData,
          createdAt: userData.createdAt ? new Date(userData.createdAt) : null,
          updatedAt: userData.updatedAt ? new Date(userData.updatedAt) : null,
          lastLoginAt: userData.lastLoginAt ? new Date(userData.lastLoginAt) : null,
        };
        setUser(user);
        
        // In remote auth mode (web), sync user data from master to local database
        // In Electron mode, the user is already loaded from local database via logged_in_user_id
        // so we just need to sync org settings
        if (isRemoteAuth && !isElectron) {
          await syncUserFromMaster(userData);
        } else {
          // In local auth mode or Electron mode, sync org settings to platform on user refresh
          await syncOrgSettingsToPlat();
        }
      } else {
        throw new Error(result.error?.message || 'Failed to fetch user');
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
      // Clear tokens and user on failure
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Login with email/password
  const login = async (credentials: LoginRequestData) => {
    try {
      // If authentication is disabled, just use mock user
      if (isAuthDisabled()) {
        console.log('Authentication disabled - login bypassed with mock user');
        setUser(createMockAdminUser());
        return;
      }

      setIsLoading(true);
      
      // Use master server for login in remote auth mode
      const loginUrl = isRemoteAuth 
        ? getMasterAuthEndpointUrl('LOGIN')
        : '/api/auth/login';
      
      const loginFn = createMutationFn<LoginResponseData, LoginRequestData>(loginUrl, 'POST');
      const result = await loginFn(credentials);

      // Store tokens
      localStorage.setItem('accessToken', result.accessToken);
      localStorage.setItem('refreshToken', result.refreshToken);
      
      // Convert date strings to Date objects to match our User interface
      const userData = result.user;
      const user: User = {
        ...userData,
        createdAt: userData.createdAt ? new Date(userData.createdAt) : null,
        updatedAt: userData.updatedAt ? new Date(userData.updatedAt) : null,
        lastLoginAt: userData.lastLoginAt ? new Date(userData.lastLoginAt) : null,
      };

      // In remote auth mode, sync user data first, then look up the LOCAL user ID
      // and use that to set logged_in_user_id (not the master server's ID)
      if (isRemoteAuth) {
        // Temporarily set user for UI feedback during sync
        setUser(user);
        
        // First sync user and org data to ensure local user exists
        await syncUserFromMaster(userData);

        // Then look up the LOCAL user by email to get the correct local user ID
        const localUserId = await getLocalUserIdByEmail(userData.email);
        if (localUserId) {
          await setLoggedInUserId(localUserId);
          
          // IMPORTANT: Refresh user from local database to get the local user ID
          // The userData has the MASTER server's ID, but we need the LOCAL ID
          // for operations like product import that use userId as a foreign key
          await refreshUser();
        } else {
          console.error('Failed to find local user by email after sync');
        }
      } else {
        setUser(user);
        // In local auth mode, sync org settings to platform after login
        await syncOrgSettingsToPlat();
      }
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Register new user
  const register = async (userData: RegisterRequestData) => {
    try {
      // If authentication is disabled, just use mock user
      if (isAuthDisabled()) {
        console.log('Authentication disabled - registration bypassed with mock user');
        setUser(createMockAdminUser());
        return;
      }

      setIsLoading(true);
      
      // Use master server for registration in remote auth mode
      const registerUrl = isRemoteAuth 
        ? getMasterAuthEndpointUrl('REGISTER')
        : '/api/auth/register';
      
      const registerFn = createMutationFn<RegisterResponseData, RegisterRequestData>(registerUrl, 'POST');
      const result = await registerFn(userData);

      // Store tokens
      localStorage.setItem('accessToken', result.accessToken);
      localStorage.setItem('refreshToken', result.refreshToken);
      
      // Convert date strings to Date objects to match our User interface
      const userResponseData = result.user;
      const user: User = {
        ...userResponseData,
        createdAt: userResponseData.createdAt ? new Date(userResponseData.createdAt) : null,
        updatedAt: userResponseData.updatedAt ? new Date(userResponseData.updatedAt) : null,
        lastLoginAt: userResponseData.lastLoginAt ? new Date(userResponseData.lastLoginAt) : null,
      };

      // In remote auth mode, sync user data first, then look up the LOCAL user ID
      // and use that to set logged_in_user_id (not the master server's ID)
      if (isRemoteAuth) {
        // Temporarily set user for UI feedback during sync
        setUser(user);
        
        // First sync user and org data to ensure local user exists
        await syncUserFromMaster(userResponseData);

        // Then look up the LOCAL user by email to get the correct local user ID
        const localUserId = await getLocalUserIdByEmail(userResponseData.email);
        if (localUserId) {
          await setLoggedInUserId(localUserId);
          
          // IMPORTANT: Refresh user from local database to get the local user ID
          // The userResponseData has the MASTER server's ID, but we need the LOCAL ID
          // for operations like product import that use userId as a foreign key
          await refreshUser();
        } else {
          console.error('Failed to find local user by email after sync');
        }
      } else {
        setUser(user);
        // In local auth mode, sync org settings to platform after registration
        await syncOrgSettingsToPlat();
      }
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout
  const logout = async () => {
    // Stop periodic re-validation timer
    stopRevalidationTimer();
    
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setUser(null);

    try {
      const { auth } = initializeFirebase();
      if (auth?.currentUser) {
        await signOut(auth);
      }
    } catch (error) {
      console.warn('Failed to sign out from Firebase during logout:', error);
    }
    
    // In remote auth mode, clear logged-in user ID from settings
    if (isRemoteAuth) {
      try {
        await fetch('/api/settings/system/logged-in-user', {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        });
      } catch (error) {
        console.error('Failed to clear logged-in user setting:', error);
      }
    }
    
    // Call logout endpoint to invalidate tokens on server
    const logoutUrl = isRemoteAuth 
      ? getMasterAuthEndpointUrl('LOGOUT')
      : '/api/auth/logout';
    fetch(logoutUrl, { method: 'POST' }).catch(console.error);
  };

  // Social login
  const socialLogin = async (provider: SocialProvider) => {
    // If authentication is disabled, just use mock user
    if (isAuthDisabled()) {
      console.log('Authentication disabled - social login bypassed with mock user');
      setUser(createMockAdminUser());
      return;
    }

    // WorkOS uses a different flow via the useWorkOSAuth hook
    // This is handled by the sign-in pages directly
    if (provider === 'workos') {
      console.warn('WorkOS login should be handled via the useWorkOSAuth hook in components wrapped by WorkOSProvider');
      throw new Error('WorkOS login must be initiated from a component using the useWorkOSAuth hook');
    }

    setIsLoading(true);

    try {
      const { auth } = initializeFirebase();

      if (!auth) {
        console.warn('Firebase authentication is not configured; falling back to server OAuth redirect');
        window.location.href = `/api/auth/${provider}`;
        return;
      }

      let firebaseUser = auth.currentUser;

      if (provider !== 'firebase') {
        let firebaseProvider: GoogleAuthProvider | OAuthProvider | null = null;

        switch (provider) {
          case 'google': {
            firebaseProvider = new GoogleAuthProvider();
            firebaseProvider.setCustomParameters?.({ prompt: 'select_account' });
            break;
          }
          case 'microsoft': {
            const microsoftProvider = new OAuthProvider('microsoft.com');
            microsoftProvider.setCustomParameters?.({ prompt: 'select_account' });
            microsoftProvider.addScope?.('User.Read');
            firebaseProvider = microsoftProvider;
            break;
          }
          default: {
            console.warn(`Unsupported social provider "${provider}" for Firebase login. Falling back to server redirect.`);
            window.location.href = `/api/auth/${provider}`;
            return;
          }
        }
        console.log(`Using social provider "${provider}" for Firebase login`);
        const credential = await signInWithPopup(auth, firebaseProvider);
        firebaseUser = credential.user;
      }

      if (!firebaseUser) {
        throw new Error('Firebase authentication failed to return a user session');
      }

      const idToken = await firebaseUser.getIdToken();

      // In remote auth mode (Electron), route Firebase verification to the master server
      // This ensures the user is created/validated on the master auth server
      const verifyUrl = isRemoteAuth 
        ? getMasterAuthEndpointUrl('FIREBASE_VERIFY')
        : '/api/auth/firebase/verify';

      const response = await fetch(verifyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Firebase verification failed: ${errorText}`);
      }

      const responseJson = await response.json();
      if (!responseJson.success) {
        throw new Error(responseJson.error?.message || 'Firebase verification failed');
      }

      const result = responseJson.data as LoginResponseData;

      localStorage.setItem('accessToken', result.accessToken);
      localStorage.setItem('refreshToken', result.refreshToken);

      const userResponse = result.user;
      const normalizedUser: User = {
        ...userResponse,
        createdAt: userResponse.createdAt ? new Date(userResponse.createdAt) : null,
        updatedAt: userResponse.updatedAt ? new Date(userResponse.updatedAt) : null,
        lastLoginAt: userResponse.lastLoginAt ? new Date(userResponse.lastLoginAt) : null,
      };

      // In remote auth mode, sync user data first, then look up the LOCAL user ID
      // and use that to set logged_in_user_id (not the master server's ID)
      if (isRemoteAuth) {
        // Temporarily set user for UI feedback during sync
        setUser(normalizedUser);
        
        // First sync user and org data to ensure local user exists
        await syncUserFromMaster(userResponse);
        
        // Then look up the LOCAL user by email to get the correct local user ID
        const localUserId = await getLocalUserIdByEmail(userResponse.email);
        if (localUserId) {
          await setLoggedInUserId(localUserId);
          
          // IMPORTANT: Refresh user from local database to get the local user ID
          // The normalizedUser has the MASTER server's ID, but we need the LOCAL ID
          // for operations like product import that use userId as a foreign key
          await refreshUser();
        } else {
          console.error('Failed to find local user by email after sync');
          // Keep the master user data as fallback
          setUser(normalizedUser);
        }
      } else {
        setUser(normalizedUser);
        // In local auth mode, sync org settings to platform after social login
        await syncOrgSettingsToPlat();
      }

      // Clear Firebase session now that Skill Shop tokens are issued
      try {
        if (auth.currentUser) {
          await signOut(auth);
        }
      } catch (cleanupError) {
        console.warn('Failed to clear Firebase session after social login:', cleanupError);
      }
    } catch (error) {
      console.error('Social login failed:', error);

      try {
        const { auth } = initializeFirebase();
        if (auth?.currentUser) {
          await signOut(auth);
        }
      } catch (signOutError) {
        console.error('Failed to sign out of Firebase after social login error:', signOutError);
      }

      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Social login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    refreshUser,
    socialLogin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Hook to check OAuth callback parameters
export function useOAuthCallback() {
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const accessToken = urlParams.get('accessToken');
    const refreshToken = urlParams.get('refreshToken');
    const provider = urlParams.get('provider');
    const error = urlParams.get('message');

    if (error) {
      console.error('OAuth error:', error);
      // Handle OAuth errors (could show toast notification)
      return;
    }

    if (accessToken && refreshToken) {
      // Store tokens and redirect to skillshop
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      
      // Clear URL parameters and redirect
      window.history.replaceState({}, document.title, window.location.pathname);
      window.location.href = '/';
    }
  }, []);
}